import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

export interface CollapseProps {
  title: React.ReactNode;
  icon?: React.ReactNode;
  description?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
  disabled?: boolean;
  /** Optional action element rendered next to the chevron (e.g. an icon button to open a drawer). */
  action?: React.ReactNode;
}

export const Collapse: React.FC<CollapseProps> = ({
  title,
  icon,
  description,
  children,
  defaultOpen = true,
  className = '',
  disabled = false,
  action,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={className}>
      {/* Header */}
      <div
        role="button"
        tabIndex={disabled ? undefined : 0}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={e => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
        className={`flex flex-row items-start justify-between w-full mb-1 group ${
          description ? '' : 'py-1'
        } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
        <div className="flex flex-col items-start gap-0.5 flex-1">
          <div className="flex flex-row items-center gap-2 w-full">
            {icon && <div className="flex items-center">{icon}</div>}
            {typeof title === 'string' ? (
              <h3 className="text-gray-900 text-[16px] font-[600] leading-[24px]">{title}</h3>
            ) : (
              title
            )}
          </div>
          {description && (
            <div
              className={`overflow-hidden transition-all duration-100 ease-out ${
                disabled || !isOpen ? 'max-h-[100px] opacity-100 mt-2' : 'max-h-0 opacity-0'
              }`}>
              <p className="text-gray-600 text-[12px] font-[400] leading-[16px]">{description}</p>
            </div>
          )}
        </div>
        <div
          className="flex items-center gap-1.5 flex-shrink-0 mt-0.5"
          onClick={e => action && e.stopPropagation()}>
          {action}
          {!disabled &&
            (isOpen ? (
              <ChevronUp className="size-5 text-gray-600 transition-transform group-hover:text-gray-900" />
            ) : (
              <ChevronDown className="size-5 text-gray-600 transition-transform group-hover:text-gray-900" />
            ))}
        </div>
      </div>

      {/* Content */}
      {!disabled && (
        <div
          className={`overflow-hidden transition-all duration-100 ease-out ${
            isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
          }`}>
          {isOpen && <div className="mt-2">{children}</div>}
        </div>
      )}
    </div>
  );
};
